import { Form, Formik } from "formik";
import ThemedModal from "../../../../@ui/Modal/ThemedModal";
import { bannerSchema } from "./BannerMessageFormSchema";
import { Button, Modal } from "react-bootstrap";
import { BannerMessage } from "../../../../types/BannerMessage/BannerMessage";
import BannerMessageForm from "./BannerMessageForm";
import { DateTime } from "luxon";
import { BannerMessageRequest } from "../../../../types/BannerMessage/BannerMessageCreateRequest";
import { BannerMessageFormModel } from "../../../../types/BannerMessage/BannerMessageForm";

interface BannerMessageFormModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  bannerMessage: BannerMessage | null;
  apiErrorMessage?: string;
}

const BannerMessageFormModal = ({
  show,
  onClose,
  onSubmit,
  bannerMessage,
  apiErrorMessage,
}: BannerMessageFormModalProps) => {
  const initialValues = bannerMessage
    ? {
        id: bannerMessage.id,
        message: bannerMessage.message,
        displayFromDate: bannerMessage.displayFrom.toISODate() || "",
        displayFromTime:
          bannerMessage.displayFrom
            .setZone("local")
            .set({ millisecond: 0 })
            .toISOTime({
              suppressMilliseconds: true,
              includeOffset: false,
            }) || "",
        displayToDate: bannerMessage.displayTo.toISODate() || "",
        displayToTime:
          bannerMessage.displayTo
            .setZone("local")
            .set({ millisecond: 0 })
            .toISOTime({
              suppressMilliseconds: true,
              includeOffset: false,
            }) || "",
      }
    : (() => {
        const now = DateTime.local().setZone("local").set({ millisecond: 0 });
        const tomorrow = now.plus({ days: 1 });

        return {
          message: "",
          displayFromDate: now.toISODate(),
          displayFromTime: now.toISOTime({
            suppressMilliseconds: true,
            includeOffset: false,
          }),
          displayToDate: tomorrow.toISODate(),
          displayToTime: tomorrow.toISOTime({
            suppressMilliseconds: true,
            includeOffset: false,
          }),
        } as BannerMessageFormModel;
      })();

  const handleFormSubmit = (values: BannerMessageFormModel) => {
    const { displayFromDate, displayFromTime, displayToDate, displayToTime } =
      values;
    const displayFrom = DateTime.fromISO(
      `${displayFromDate}T${displayFromTime}`,
      { zone: "local" }
    ).toUTC();
    const displayTo = DateTime.fromISO(`${displayToDate}T${displayToTime}`, {
      zone: "local",
    }).toUTC();

    const requestPayload: BannerMessageRequest = {
      id: values.id,
      message: values.message,
      displayFrom: displayFrom!.toISO()!,
      displayTo: displayTo!.toISO()!,
    };

    onSubmit(requestPayload);
  };

  return (
    <ThemedModal centered show={show} onHide={onClose}>
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues ?? {}}
        validationSchema={bannerSchema}
      >
        {({ handleSubmit, values }) => (
          <Form onSubmit={handleSubmit}>
            <Modal.Header>
              <Modal.Title>
                {bannerMessage ? "Edit" : "Create"} Banner Message
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {apiErrorMessage && (
                <div className="p-1 bg-danger text-white mb-2">
                  {apiErrorMessage}
                </div>
              )}
              <BannerMessageForm />
            </Modal.Body>
            <Modal.Footer>
              <Button
                className="text-white"
                variant="secondary"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button className="text-white" type="submit">
                {bannerMessage ? "Update" : "Create"}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </ThemedModal>
  );
};

export default BannerMessageFormModal;
